import {
  PropertyFilterProps,
  TableProps,
  CollectionPreferences,
  StatusIndicator,
} from '@cloudscape-design/components';
import { Category } from '../../types/helios/Category';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import Toggle from '@cloudscape-design/components/toggle';
import React from 'react';
import { FilterItemType } from 'src/hooks/useGenericFilter';
import { createDateFilterConfig } from 'src/components/filter/FilterDatePicker';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import { formatDateTime } from 'src/utils/generalUtils';

export enum CategoryPropertyKey {
  BUSINESS_UNIT = 'businessUnit',
  DIVISION = 'divisionName',
  ACTIVE_STATUS = 'parent.activeStatus',
  LAST_UPDATED_AT = 'lastUpdatedAt',
  CREATED_AT = 'createdAt',
}

export const initialFilters = [
  {
    propertyKey: CategoryPropertyKey.ACTIVE_STATUS,
    value: 'true',
    label: 'Active',
  },
  {
    propertyKey: CategoryPropertyKey.ACTIVE_STATUS,
    value: 'false',
    label: 'Inactive',
  },
];
export const getCategoryColumnDefinitions = (
  toggleCategoryStatus: (id: string, status: boolean) => void,
  businessUnitList: BusinessUnit[] = [],
): TableProps.ColumnDefinition<Category>[] => {
  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return [
    {
      id: 'category',
      header: 'Question Categories',
      cell: (item: Category) => item.categoryName,
      isRowHeader: true,
    },
    {
      id: 'businessUnit',
      header: 'Business Unit',
      cell: (item: Category) => getBusinessUnitName(item.businessUnit),
    },
    {
      id: 'division',
      header: 'Division',
      cell: (item: Category) => item.divisionName,
    },
    {
      id: 'requiredTags',
      header: 'Required Tag Groups',
      cell: (item: Category) =>
        Array.isArray(item.requiredTags)
          ? item.requiredTags
              .map((tag) => {
                if (tag === null || tag === undefined) return '';
                return tag.tagGroupName || '';
              })
              .filter(Boolean)
              .join(', ')
          : '',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: Category) => (
        <StatusIndicator type={item.isActive ? 'success' : 'error'}>
          {item.isActive ? 'Active' : 'Inactive'}
        </StatusIndicator>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created At',
      cell: (item: Category) => (item.createdAt ? formatDateTime(item.createdAt) : '-'),
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: Category) => item.createdBy || '-',
    },
    {
      id: 'lastUpdatedAt',
      header: 'Last Updated At',
      cell: (item: Category) =>
        item.lastUpdatedAt ? formatDateTime(item.lastUpdatedAt) : '-',
    },
    {
      id: 'lastUpdatedBy',
      header: 'Last Updated By',
      cell: (item: Category) => item.lastUpdatedBy || '-',
    },
  ];
};

export const getExportCategoryConfig = (businessUnitList: BusinessUnit[] = []) => {
  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return {
    fileName: 'question_categories.csv',
    mappings: {
      'Category': 'categoryName',
      'Business Unit': (category: Category) => getBusinessUnitName(category.businessUnit),
      'Division': 'divisionName',
      'Required Tags': (category: Category) =>
        category?.requiredTags
          ?.map((tag) => tag?.tagGroupName || '')
          .filter(Boolean)
          .join(', ') || '',
      'Status': (category: Category) => (category?.isActive ? 'Active' : 'Inactive'),
      'Created By': 'createdBy',
      'Created At': (category: Category) =>
        category.createdAt ? formatDateTime(category.createdAt) : '-',

      'Last Updated At': (category: Category) =>
        category.lastUpdatedAt ? formatDateTime(category.lastUpdatedAt) : '-',
      'Last Updated By': 'lastUpdatedBy',
    },
  };
};

export const getFilterCategoriesProperties = (isPlatformAdmin: boolean): any[] => {
  const baseFilters: any[] = [
    {
      key: CategoryPropertyKey.DIVISION,
      propertyLabel: 'Division',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Division values',
    },
    {
      key: CategoryPropertyKey.ACTIVE_STATUS,
      propertyLabel: 'Status',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Status values',
    },
    createDateFilterConfig({
      key: CategoryPropertyKey.CREATED_AT,
      propertyLabel: 'Created At',
      groupValuesLabel: 'Date',
    }) as any,
    createDateFilterConfig({
      key: CategoryPropertyKey.LAST_UPDATED_AT,
      propertyLabel: 'Last Updated At',
      groupValuesLabel: 'Date',
    }) as any,
  ];

  if (isPlatformAdmin) {
    return [
      ...baseFilters,
      {
        key: CategoryPropertyKey.BUSINESS_UNIT,
        propertyLabel: 'Business Unit',
        operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
        groupValuesLabel: 'Business Unit values',
      },
    ];
  }

  return baseFilters;
};
export const getItemValue = (item: Category, propertyKey: string): any => {
  if (!item) return { itemValue: '' };

  switch (propertyKey) {
    case CategoryPropertyKey.BUSINESS_UNIT:
      return {
        itemValue: item.businessUnit,
        type: FilterItemType.TEXT,
      };

    case CategoryPropertyKey.DIVISION:
      return {
        itemValue: item.divisionName,
        type: FilterItemType.TEXT,
      };

    case CategoryPropertyKey.ACTIVE_STATUS:
      return {
        itemValue: item.isActive,
        type: FilterItemType.BOOLEAN,
      };

    case CategoryPropertyKey.LAST_UPDATED_AT:
      // Temporary debug logging to understand date format
      console.log('Categories lastUpdatedAt debug:', {
        raw: item.lastUpdatedAt,
        type: typeof item.lastUpdatedAt,
        parsed: item.lastUpdatedAt ? new Date(item.lastUpdatedAt) : null,
        isValid: item.lastUpdatedAt
          ? !isNaN(new Date(item.lastUpdatedAt).getTime())
          : false,
      });
      return {
        itemValue: item.lastUpdatedAt,
        type: FilterItemType.DATE,
      };
    case CategoryPropertyKey.CREATED_AT:
      return {
        itemValue: item.createdAt,
        type: FilterItemType.DATE,
      };
    default:
      return { itemValue: '' };
  }
};
export const getSearchableFields = (item: Category) => [
  item.categoryName || '',
  item.divisionName || '',
  item.description || '',
  item.businessUnit || '',
  item.createdBy || '',
];

export const contentDisplayPreference = {
  title: 'Column preferences',
  description: 'Customize which columns are shown',
  options: [
    { id: 'category', label: 'Question Categories' },
    { id: 'businessUnit', label: 'Business Unit' },
    { id: 'division', label: 'Division' },
    { id: 'requiredTags', label: 'Required Tag Groups' },
    { id: 'status', label: 'Status' },
    { id: 'createdAt', label: 'Created At' },
    { id: 'createdBy', label: 'Created By' },
    { id: 'lastUpdatedAt', label: 'Last Updated At' },
    { id: 'lastUpdatedBy', label: 'Last Updated By' },
  ],
};

export const contentDisplay = [
  { id: 'category', visible: true },
  { id: 'businessUnit', visible: true },
  { id: 'division', visible: true },
  { id: 'requiredTags', visible: true },
  { id: 'status', visible: true },
  { id: 'createdAt', visible: false },
  { id: 'createdBy', visible: false },
  { id: 'lastUpdatedAt', visible: false },
  { id: 'lastUpdatedBy', visible: false },
];

export const pageSizePreference = {
  title: 'Page size preferences',
  options: [
    { value: 10, label: '10 items' },
    { value: 20, label: '20 items' },
    { value: 30, label: '30 items' },
    { value: 50, label: '50 items' },
    { value: 100, label: '100 items' },
  ],
};

// Get predefined filter options for categories
export const getCategoryFilterOptions = (categoriesList: Category[] = []) => {
  // Get unique division names from the current categories list
  const uniqueDivisions = new Set(
    categoriesList.map((category) => category.divisionName),
  );
  const divisionOptions = Array.from(uniqueDivisions)
    .filter(Boolean)
    .map((divisionName) => ({
      propertyKey: CategoryPropertyKey.DIVISION,
      value: divisionName,
      label: divisionName,
    }));

  // Get unique business units from the current categories list
  const uniqueBusinessUnits = new Set(
    categoriesList.map((category) => category.businessUnit),
  );
  const businessUnitOptions = Array.from(uniqueBusinessUnits)
    .filter(Boolean)
    .map((businessUnit) => ({
      propertyKey: CategoryPropertyKey.BUSINESS_UNIT,
      value: businessUnit,
      label: businessUnit,
    }));

  return divisionOptions.concat(businessUnitOptions).concat(initialFilters);
};

export const createCategoryPreferences = (
  pageSize: number,
  visibleColumns: any[],
  onConfirm: (detail: any) => void,
) => {
  return React.createElement(CollectionPreferences, {
    title: 'Preferences',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    preferences: {
      pageSize: pageSize,
      contentDisplay: visibleColumns,
    },
    pageSizePreference: pageSizePreference,
    contentDisplayPreference: contentDisplayPreference,
    onConfirm: ({ detail }: any) => onConfirm(detail),
  });
};
